import Joi from "joi";

const dischargePayloadSchema = (label) =>
  Joi.object({
    description: Joi.string()
      .required()
      .label(`${label} description`)
      .messages({
        "any.required": "{#label} is required",
        "string.empty": "{#label} cannot be empty",
      }),
    createdDateTime: Joi.date()
      .iso()
      .required()
      .label(`${label} createdDateTime`)
      .messages({
        "any.required": "{{#label}} is required",
        "date.format": "{{#label}} must be in ISO format",
      }),
    createdBy: Joi.string().required().label(`${label} createdBy`).messages({
      "any.required": "{{#label}} is required",
    }),
  });

const medicineSchema = Joi.object({
  dosage: Joi.string().required().label("dosage").messages({
    "any.required": "{{#label}} is required",
  }),
  quantity: Joi.number().min(1).required().label("quantity").messages({
    "any.required": "{{#label}} is required",
    "number.min": "{{#label}} must be at least 1",
  }),
  days: Joi.number().min(1).required().label("days").messages({
    "any.required": "{{#label}} is required",
    "number.min": "{{#label}} must be at least 1",
  }),
  createdDateTime: Joi.date()
    .iso()
    .required()
    .label("medicine createdDateTime"),
  createdBy: Joi.string().required().label("medicine createdBy"),
  item: Joi.string().required().label("medicine item"),
});

const generalExamSchema = Joi.object({
  pulse: Joi.number().required().messages({
    "number.base": "Pulse must be a number",
    "any.required": "Pulse is required",
  }),
  bloodPressure: Joi.string().required().messages({
    "string.base": "Blood Pressure must be a string",
    "any.required": "Blood Pressure is required",
  }),
  resp: Joi.number().required().messages({
    "number.base": "Respiration rate must be a number",
    "any.required": "Respiration rate is required",
  }),
  temperature: Joi.number().required().messages({
    "number.base": "Temperature must be a number",
    "any.required": "Temperature is required",
  }),
  spo2: Joi.number().required().messages({
    "number.base": "SpO2 must be a number",
    "any.required": "SpO2 is required",
  }),
  createdDateTime: Joi.date().iso().required(),
  createdBy: Joi.string().required(),
});

const otherExamSchema = Joi.object({
  chest: Joi.string().required().messages({
    "string.base": "Chest must be a string",
    "any.required": "Chest is required",
  }),
  cvs: Joi.string().required().messages({
    "string.base": "CVS must be a string",
    "any.required": "CVS is required",
  }),
  abdomen: Joi.string().required().messages({
    "string.base": "Abdomen must be a string",
    "any.required": "Abdomen is required",
  }),
  cns: Joi.string().required().messages({
    "string.base": "CNS must be a string",
    "any.required": "CNS is required",
  }),
  investigations: Joi.string().required().messages({
    "string.base": "Investigations must be a string",
    "any.required": "Investigations is required",
  }),
  createdDateTime: Joi.date().iso().required(),
  createdBy: Joi.string().required(),
});

const followUpSchema = Joi.object({
  followUpDate: Joi.date().iso().required().messages({
    "date.base": "Follow Up Date must be a valid ISO date",
    "any.required": "Follow Up Date is required",
  }),
  followUpTime: Joi.string().required().messages({
    "string.base": "Follow Up Time must be a string",
    "any.required": "Follow Up Time is required",
  }),
  doctor: Joi.string().required().messages({
    "string.base": "Doctor ID must be a string",
    "any.required": "Doctor is required",
  }),
  createdDateTime: Joi.date().iso().required(),
  createdBy: Joi.string().required(),
});

const dischargeDetailsSchema = Joi.object({
  approvedBy: Joi.string().required().messages({
    "string.base": "ApprovedBy must be a string",
    "any.required": "ApprovedBy is required",
  }),
  doctorName: Joi.string().required().messages({
    "string.base": "Doctor Name must be a string",
    "any.required": "Doctor Name is required",
  }),
  doctorSignature: Joi.string().allow("").messages({
    "string.base": "Doctor Signature must be a string",
  }),
});

const dischargeSummarySchema = Joi.object({
  finalDiagnosis: Joi.array()
    .items(dischargePayloadSchema("Final Diagnosis"))
    .required()
    .label("Final Diagnosis"),
  presentingComplaint: Joi.array()
    .items(dischargePayloadSchema("Presenting Complaint"))
    .required()
    .label("Presenting Complaint"),
  pastMedicalHistory: Joi.array()
    .items(dischargePayloadSchema("Past Medical History"))
    .required()
    .label("pastMedicalHistory"),
  allergies: Joi.array()
    .items(dischargePayloadSchema("Allergies"))
    .required()
    .label("allergies"),
  medicinesSuggested: Joi.object({
    inhouseMedicines: Joi.array().items(medicineSchema).required(),
    externalMedicines: Joi.array().items(medicineSchema).required(),
  }).required(),
  onAdmission: Joi.object({
    generalExamination: generalExamSchema.required(),
    other: otherExamSchema.required(),
  }).required(),

  dischargeConditions: Joi.object({
    courseAndTreatmentInHospital: Joi.array()
      .items(dischargePayloadSchema("Course and Treatment In Hospital"))
      .required(),
    conditionAtDischarge: Joi.array()
      .items(dischargePayloadSchema("Condition At Discharge"))
      .required(),
    adviceOnDischarge: Joi.array()
      .items(dischargePayloadSchema("Advice On Discharge"))
      .required(),
  }).required(),

  followUp: Joi.array().items(followUpSchema).required().label("followUp"),

  dischargeDetails: dischargeDetailsSchema.required(),
});
export default dischargeSummarySchema;
